import * as React from 'react'
import { Option } from 'widgets-for-react'
import { datalayerHandler } from '../../../utils/datalayer'
import { useInView } from '../../../utils/hooks/hooks/useInView'
import { Image, ImageBase, ImageOptions, urlToImage } from '../../../utils/images'
import { Link } from '../../../utils/links'
import { maybeToMailPlusLink } from '../../../utils/mailplus'
import { ctaBlockClasses } from '../../../utils/style'
import { getClassName, htmlDecode, unEntity } from '../../../utils/utils'
import { ContactPersonData } from '../sharepointPageApi/types/pageData'
import { ActionProps, InviewProps } from './blocksState'

export type CtaBlock = {
  index: number
  title?: React.ReactNode
  intro: string
  buttons?: 'none' | 'primary' | 'primary_secondary'
  image?: ImageBase & ImageOptions
  type: 'big' | 'small',
  primaryAction?: ActionProps
  secondaryAction?: ActionProps
  inview?: InviewProps
  phone?: { title: string, text: string }
}

export type CtaBlockStyle = Partial<{
  blockContentWidthClassName: string
  blockFullWidthClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  contentClassName: string
  textClassName: string
  footerClassName: string
  visualClassName: string
  imageClassName: string
  imageNonExpandableClassName: string
  pictureClassName: string
  clickableWrapperClassName: string
  clickablePrimaryClassName: string
  clickableSecondaryClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  iconPrimaryId: string
  iconSecondaryId: string
}>

export const CtaBlockLayout = (
  props: CtaBlock
) => {
  const {
    image,
    inview,
    index,
    intro,
    primaryAction,
    secondaryAction,
    title,
    phone,
    buttons,
    type
  } = props;

  let adjustedImage = image;


  const classes = ctaBlockClasses(props.image !== undefined)
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("CtaBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  const typeClass = type === 'small' ? ' cta--small' : ' cta--big'
  const inviewClassName = getClassName(classes, 'blockContentWidthClassName', 'container') + typeClass + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')

  return (
    <section key={`block-${index}`} id={`io-${index}`} ref={ref} className={inviewClassName}>
      <div
        className={getClassName(
          classes,
          'innerClassName',
          'd-md-flex align-items-center bg-light'
        )}
      >
        <div
          className={getClassName(
            classes,
            'contentClassName',
            'p-5 flex-grow-1'
          )}
        >
          {title && (
            <header className={getClassName(classes, 'headerClassName', '')}>
              <h2
                className={getClassName(
                  classes,
                  'titleClassName',
                  'text-center mb-3'
                )}
                id={`${index}`}
              >
                {typeof title === 'string' ? htmlDecode(title) : title}
              </h2>
            </header>
          )}

          {intro && (
            <p
              className={getClassName(
                classes,
                'textClassName',
                'lead text-center'
              )}
            >
              {unEntity(intro)}
            </p>
          )}

          {((primaryAction && primaryAction.title) ||
            (secondaryAction && secondaryAction.title) ||
            (phone && phone.text && phone.title)) && (
            <footer
              className={getClassName(
                classes,
                'footerClassName',
                'text-center'
              )}
            >
              {buttons !== 'none' &&
              primaryAction &&
              primaryAction.title && (
                <div
                  className={getClassName(
                    classes,
                    'clickableWrapperClassName',
                    'd-inline-block'
                  )}
                >
                  <Link
                    title={primaryAction.title}
                    url={primaryAction.url}
                    className={
                       getClassName(
                            classes,
                            'clickablePrimaryClassName',
                            'btn btn-dark mr-2 d-inline-flex'
                        )
                    }
                    onClick={() =>
                      datalayerHandler('cta', primaryAction.title, 'cta', {
                        link: maybeToMailPlusLink(primaryAction.url)
                      })
                    }
                  >
                    <span
                      className={getClassName(
                        classes,
                        'clickableLabelClassName',
                        ''
                      )}
                    >
                      {htmlDecode(primaryAction.title)}
                    </span>
                    {primaryAction.SVGIcon && (
                      <svg
                        className={getClassName(
                          classes,
                          'clickableIconClassName',
                          'ml-3'
                        )}
                        aria-hidden="true"
                        width={
                          primaryAction.SVGIcon.width
                            ? primaryAction.SVGIcon.width
                            : '24px'
                        }
                        height={
                          primaryAction.SVGIcon.height
                            ? primaryAction.SVGIcon.height
                            : '24px'
                        }
                      >
                        <use
                          xlinkHref={getClassName(
                            classes,
                            'iconPrimaryId',
                            '#icon--chevron-right-large'
                          )}
                        />
                      </svg>
                    )}
                  </Link>
                </div>
              )}

              {/* [not CUSTOM & HARCODED anymore */}
              {image && phone && phone.text && phone.title && ( // Should be based on check if we show a button or phone field
                <div className="block__clickable cta-phone">
                  <p className="cta-phone__title title--xs">Of bel ons op: <a className="cta-phone__link" href={`tel:${phone.title}`}>{phone.title}</a></p>
                  <p className="cta-phone__text text--xs">{phone.text}</p>
                </div>
              )}

              {!image && // TEMP! Should be based on check if we show a button or phone field (see above)
                buttons == 'primary_secondary' &&
                secondaryAction &&
                secondaryAction.title && (
                  <div
                    className={getClassName(
                      classes,
                      'clickableWrapperClassName',
                      'd-inline-block'
                    )}
                  >
                    <Link
                      url={secondaryAction.url}
                      title={secondaryAction.title}
                      className={getClassName(
                        classes,
                        'clickableSecondaryClassName',
                        'btn btn-link px-0 mr-2 d-inline-flex'
                      )}
                      onClick={() =>
                        datalayerHandler('cta', secondaryAction.title, 'cta', {
                          link: maybeToMailPlusLink(secondaryAction.url)
                        })
                      }
                    >
                      <span
                        className={getClassName(
                          classes,
                          'clickableLabelClassName',
                          ''
                        )}
                      >
                        {htmlDecode(secondaryAction.title)}
                      </span>
                      {secondaryAction.SVGIcon && (
                        <svg
                          className={getClassName(
                            classes,
                            'clickableIconClassName',
                            'ml-3'
                          )}
                          aria-hidden="true"
                          width={
                            secondaryAction.SVGIcon.width
                              ? secondaryAction.SVGIcon.width
                              : '24px'
                          }
                          height={
                            secondaryAction.SVGIcon.height
                              ? secondaryAction.SVGIcon.height
                              : '24px'
                          }
                        >
                          <use
                            xlinkHref={getClassName(
                              classes,
                              'iconSecondaryId',
                              '#icon--chevron-right-large'
                            )}
                          />
                        </svg>
                      )}
                  </Link>
                </div>
              )}
            </footer>
          )}
        </div>

        {adjustedImage && (
          <div
            className={getClassName(classes, 'visualClassName', 'flex-grow-0')}
          >
            <Image
              {...adjustedImage}
              classNames={{
                image: getClassName(
                  classes,
                  (adjustedImage?.keepOriginalSize ? 'imageNonExpandableClassName' :'imageClassName' )
                ),
                picture: getClassName(classes, 'pictureClassName', ''),
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}

export const getCtaBlockLayoutFromContactPerson = (cp: Option<ContactPersonData>) => cp.kind === "r" ? (
  <CtaBlockLayout
    title={cp.v.Title}
    intro={cp.v.Intro || ""}
    phone={{ title: cp.v.PhoneNumber || "", text: cp.v.PhoneText || ""}}
    primaryAction={{ title: cp.v.PrimaryButtonTitle || "", url: cp.v.PrimaryButtonUrl?.Url || "" }}
    type='big'
    secondaryAction={{ title: cp.v.SecondaryButtonTitle || "", url: cp.v.SecondaryButtonUrl?.Url || "" }}
    index={0}
    image={cp.v.ImageUrl ? urlToImage(cp.v.ImageUrl.Url, { alt: cp.v.ImageUrl.Title }) : undefined}
  />
) : <></>
